import { batch } from 'editor/src/store/batchedSubscribeEnhancer';
import { updateMediaElementAction } from 'editor/src/store/design/slice';
import { getSharedTextFieldMembers } from 'editor/src/store/design/util/sharedTextFields';
import saveUndoRedoStateOperation from 'editor/src/store/editorModules/undoRedo/operation/saveUndoRedoStateOperation';
import type { Thunk } from 'editor/src/store/hooks';

/**
 * Breaks a whole group up at once: every member keeps the string it is holding
 * and stops sharing it.
 *
 * The per-layer route out — updateSharedTextFieldOperation with no target —
 * removes ONE member, which is the right move from a layer's own panel. Undoing
 * a group of three from there takes two of those and leaves two undo entries
 * behind, the first of them a state the author never asked for: a group of two
 * they had not decided to keep. One write, one undo entry.
 *
 * No value moves and no geometry changes, so nothing here needs re-measuring or
 * a reflow pass — unlike the join, which pours a value across the group.
 */
const dissolveSharedTextFieldOperation =
  (sharedFieldId: string): Thunk =>
  (dispatch, getState) => {
    const members = getSharedTextFieldMembers(getState().design.designData, sharedFieldId);
    if (!members.length) {
      return;
    }

    batch(() => {
      dispatch(saveUndoRedoStateOperation('Shared field updated'));
      members.forEach(({ element, address }) =>
        dispatch(
          updateMediaElementAction({
            elementAddress: address,
            elementUpdate: { extra: { ...element.extra, sharedFieldId: undefined } },
          }),
        ),
      );
    });
  };

export default dissolveSharedTextFieldOperation;
